import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { activityActionLabel, activityGroupLabel } from '../lib/types';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import type { AuditPage as AuditPageData, AuditVocabulary, CompanySummary, PersonRow } from '../lib/types';

/**
 * The whole audit trail, filtered. Administrators only.
 *
 * Everything in the Hub has been written here since the first migration — every edit, every
 * approval, every sign-in — and until now none of it could be read back. This is the answer to
 * "who changed that, and when", which is the question an append-only log exists to answer.
 *
 * FILTERS NARROW ON THE SERVER, not in the browser. The table has a row for every action
 * anybody has ever taken, so fetching it all and filtering here would get slower every week.
 */
export function AuditPage() {
  /* A page that IS a table: it gets the window, not the reading column. */
  const { session } = useSession();
  const [data, setData] = useState<AuditPageData | null>(null);
  const [vocabulary, setVocabulary] = useState<AuditVocabulary | null>(null);
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);

  const isAdmin = !!session?.person.isAdmin;
  const LIMIT = 50;

  /**
   * The filters as a query string, built once and used by both the page and the export — so
   * the spreadsheet can never contain something different from what is on screen.
   */
  const filterQuery = useCallback(() => {
    const query = new URLSearchParams();
    for (const [key, value] of [
      ['from', from],
      ['to', to],
      ['actorId', actorId],
      ['action', action],
      ['companyId', companyId],
      ['q', q.trim()],
    ]) {
      if (value) query.set(key, value);
    }
    return query;
  }, [from, to, actorId, action, companyId, q]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    const query = filterQuery();
    query.set('limit', String(LIMIT));
    query.set('offset', String(offset));

    try {
      setData(await api.get<AuditPageData>(`/audit?${query}`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the log');
    }
  }, [isAdmin, filterQuery, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  /** The pickers, once. Their contents do not change while somebody is filtering. */
  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      try {
        const [vocab, peopleRows, companyRows] = await Promise.all([
          api.get<AuditVocabulary>('/audit/vocabulary'),
          api.get<PersonRow[]>('/people'),
          api.get<CompanySummary[]>('/companies'),
        ]);
        setVocabulary(vocab);
        setPeople(peopleRows);
        setCompanies(companyRows);
      } catch {
        // The filters degrade to text and dates, which still work.
      }
    })();
  }, [isAdmin]);

  /** Any change to a filter starts again at the first page, or page 4 of the old query shows. */
  const change = (apply: () => void) => {
    apply();
    setOffset(0);
  };

  /**
   * What a recorded event shows.
   *
   * `when` sorts on the stored timestamp, not on the formatted string — "01/09/2026 14:03"
   * sorts as text by day-of-month, which would interleave September with January.
   */
  const columns: Column<AuditPageData['events'][number]>[] = useMemo(
    () => [
      {
        key: 'when',
        header: 'When',
        width: '170px',
        value: (event) => event.at,
        filter: 'none',
        render: (event) => (
          <span className="mono" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
            {when(event.at)}
          </span>
        ),
      },
      {
        key: 'who',
        header: 'Who',
        value: (event) => event.who,
        filter: 'text',
        render: (event) => (
          <>
            {event.who}
            {event.ipAddress && (
              <div className="dim mono" style={{ fontSize: 11 }}>
                {event.ipAddress}
              </div>
            )}
          </>
        ),
      },
      {
        key: 'what',
        header: 'What',
        value: (event) => activityActionLabel(event.action),
        filter: 'choice',
        render: (event) => <span className="badge">{activityActionLabel(event.action)}</span>,
      },
      {
        key: 'on',
        header: 'On',
        width: '340px',
        value: (event) => event.entityLabel || event.entityType,
        filter: 'text',
        render: (event) => <span className="truncate">{event.entityLabel || event.entityType}</span>,
      },
    ],
    [],
  );

  /*
    Above the early return, not below it. `isAdmin` is false until the session arrives, so a
    memo down there would run on the second render and not the first — "Rendered more hooks
    than during the previous render".
  */
  if (!isAdmin) {
    return (
      <div className="page-head">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>User activity log</h1>
          <p className="subtitle">The activity log is for administrators.</p>
        </div>
      </div>
    );
  }

  const filtered = !!(from || to || actorId || action || companyId || q.trim());

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Administration</div>
          <h1>User activity log</h1>
          <p className="subtitle">
            Every change anybody has made, append-only and never edited. Narrow it by date,
            person, kind or company — or search what a thing was called at the time.
          </p>
        </div>
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      <div className="card mb-2">
        <div className="card-pad">
          <div className="row wrap" style={{ gap: 10, alignItems: 'flex-end' }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span className="field-label">From</span>
              <input type="date" value={from} max={to || undefined} onChange={(e) => change(() => setFrom(e.target.value))} />
            </label>
            <label className="field" style={{ marginBottom: 0 }}>
              <span className="field-label">To</span>
              <input type="date" value={to} min={from || undefined} onChange={(e) => change(() => setTo(e.target.value))} />
            </label>

            <label className="field" style={{ marginBottom: 0, minWidth: 170 }}>
              <span className="field-label">Person</span>
              <select value={actorId} onChange={(e) => change(() => setActorId(e.target.value))}>
                <option value="">Anybody</option>
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field" style={{ marginBottom: 0, minWidth: 170 }}>
              <span className="field-label">Kind</span>
              {/*
                Grouped by the first segment — "work_item", "risk", "auth" — because that is
                how people ask the question. The server matches it as a prefix, so choosing
                Work finds every kind of work event rather than one exact name.
              */}
              <select value={action} onChange={(e) => change(() => setAction(e.target.value))}>
                <option value="">Everything</option>
                {(vocabulary?.groups ?? []).map((group) => (
                  <option key={group} value={group}>
                    {activityGroupLabel(group)}
                  </option>
                ))}
              </select>
            </label>

            <label className="field" style={{ marginBottom: 0, minWidth: 170 }}>
              <span className="field-label">Company</span>
              <select value={companyId} onChange={(e) => change(() => setCompanyId(e.target.value))}>
                <option value="">All companies</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field grow" style={{ marginBottom: 0, minWidth: 200 }}>
              <span className="field-label">Search</span>
              <input
                type="search"
                value={q}
                placeholder="A task key, a title, a name"
                onChange={(e) => change(() => setQ(e.target.value))}
              />
            </label>

            {filtered && (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() =>
                  change(() => {
                    setFrom('');
                    setTo('');
                    setActorId('');
                    setAction('');
                    setCompanyId('');
                    setQ('');
                  })
                }
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>{data ? `${data.total.toLocaleString('en-ZA')} entries` : 'Entries'}</h3>
          <span className="row" style={{ gap: 10 }}>
            {data && data.total > 0 && (
              <span className="dim" style={{ fontSize: 12 }}>
                showing {data.offset + 1}–{data.offset + data.events.length}
              </span>
            )}
            {/*
              Every row the filters match, not the page on screen — an export of the fifty
              somebody happened to be looking at is worse than none, because it looks like the
              whole answer. A plain link, so the browser's own download handles it.
            */}
            {data && data.total > 0 && (
              <a
                href={`/api/audit.csv?${filterQuery()}`}
                className="btn btn-quiet btn-sm"
                title="Opens in Excel. Exports everything these filters match, not just this page."
              >
                Export {data.total.toLocaleString('en-ZA')} to CSV
              </a>
            )}
          </span>
        </div>

        <div className="card-pad">
          {!data ? (
            <span className="dim">Loading…</span>
          ) : data.events.length === 0 ? (
            <p className="dim" style={{ margin: 0, fontSize: 13 }}>
              {filtered ? 'Nothing matches those filters.' : 'Nothing has been recorded yet.'}
            </p>
          ) : (
            <DataTable
              tableKey="audit"
              columns={columns}
              rows={data.events}
              /*
                Its own search and export are off. This page narrows on the SERVER — the table
                has a row for every action anybody has ever taken — and its export goes back to
                the API for up to 50,000 rows, which a browser-side one could never match. What
                the grid adds here is sorting, per-column narrowing of the page in front of you,
                and a column chooser.
              */
              showSearch={false}
              showExport={false}
              showPaging={false}
              expand={{
                has: (event) => !!event.details && Object.keys(event.details).length > 0,
                render: (event) => <pre className="audit-detail">{JSON.stringify(event.details, null, 2)}</pre>,
              }}
            />
          )}

          {data && (data.offset > 0 || data.hasMore) && (
            <div className="row mt-2" style={{ gap: 7 }}>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={data.offset === 0}
                onClick={() => setOffset(Math.max(data.offset - LIMIT, 0))}
              >
                Newer
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={!data.hasMore}
                onClick={() => setOffset(data.offset + LIMIT)}
              >
                Older
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function when(at: string): string {
  const date = new Date(at);
  return `${date.toLocaleDateString('en-ZA')} ${date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}
